import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import GoonghapIntro from './components/GoonghapIntro';
import GoonghapResult from './components/GoonghapResult';
import BirthDateInput from '@/pages/home/components/BirthDateInput';
import BirthTimeInput from '@/pages/home/components/BirthTimeInput';
import BirthLocationInput from '@/pages/home/components/BirthLocationInput';
import LoadingAnimation from '@/pages/home/components/LoadingAnimation';
import { calculateCorrection, calculateSaju } from '@/utils/sajuCalculations';
import { calculateGoonghapScores } from '@/utils/goonghapCalculations';
import type { SajuResult, CorrectionResult } from '@/utils/sajuCalculations';
import type { LocationData } from '@/pages/home/components/BirthLocationInput';

type CalendarType = 'solar' | 'lunar' | 'lunarLeap';
type GStep = 'intro' | 'a_date' | 'a_time' | 'a_location' | 'b_date' | 'b_time' | 'b_location' | 'loading' | 'result';

interface PersonData {
  year: number; month: number; day: number; calendarType: CalendarType;
  hour: number; minute: number; unknownTime: boolean;
  location: LocationData | null;
  saju: SajuResult | null;
  correction: CorrectionResult | null;
}

const DEFAULT_PERSON: PersonData = {
  year: 1993, month: 3, day: 10, calendarType: 'solar',
  hour: 10, minute: 30, unknownTime: false,
  location: null, saju: null, correction: null,
};

// 진행 단계 표시 (0~7 = intro~result)
const STEP_INDEX: Record<GStep, number> = {
  intro: 0, a_date: 1, a_time: 2, a_location: 3,
  b_date: 4, b_time: 5, b_location: 6,
  loading: 7, result: 8,
};

function PersonBanner({ label, name }: { label: string; name: string }) {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';
  return (
    <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-100 px-5 py-2.5 flex items-center justify-between pointer-events-none">
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-black text-white ${label === 'A' ? 'bg-gray-900' : 'bg-rose-500'}`}>{label}</div>
        <span className="text-sm font-bold text-gray-900">
          {isEn ? `Entering ${name}'s Info` : `${name}님 정보 입력 중`}
        </span>
      </div>
      <span className="text-xs text-gray-400">{label === 'A' ? '1/2' : '2/2'}</span>
    </div>
  );
}

export default function GoonghapPage() {
  const [step, setStep] = useState<GStep>('intro');
  const [nameA, setNameA] = useState('');
  const [nameB, setNameB] = useState('');
  const [personA, setPersonA] = useState<PersonData>({ ...DEFAULT_PERSON });
  const [personB, setPersonB] = useState<PersonData>({ ...DEFAULT_PERSON, year: 1995, month: 7, day: 22 });
  const [goonghapScores, setGoonghapScores] = useState<ReturnType<typeof calculateGoonghapScores> | null>(null);

  const go = useCallback((s: GStep) => { setStep(s); window.scrollTo({ top: 0 }); }, []);

  const handleIntroStart = (a: string, b: string) => { setNameA(a); setNameB(b); go('a_date'); };

  const handleADate = (d: { year: number; month: number; day: number; calendarType: CalendarType }) => {
    setPersonA(p => ({ ...p, ...d }));
    go('a_time');
  };
  const handleATime = (d: { hour: number; minute: number; unknownTime: boolean }) => {
    setPersonA(p => ({ ...p, ...d }));
    go('a_location');
  };
  const handleALocation = (loc: LocationData) => {
    setPersonA(p => ({ ...p, location: loc }));
    go('b_date');
  };

  const handleBDate = (d: { year: number; month: number; day: number; calendarType: CalendarType }) => {
    setPersonB(p => ({ ...p, ...d }));
    go('b_time');
  };
  const handleBTime = (d: { hour: number; minute: number; unknownTime: boolean }) => {
    setPersonB(p => ({ ...p, ...d }));
    go('b_location');
  };
  const handleBLocation = (loc: LocationData) => {
    const updatedB = { ...personB, location: loc };
    setPersonB(updatedB);

    // 두 사람 계산
    const corrA = calculateCorrection(personA.year, personA.month, personA.day, personA.unknownTime ? 12 : personA.hour, personA.unknownTime ? 0 : personA.minute, personA.location!.longitude);
    const corrB = calculateCorrection(updatedB.year, updatedB.month, updatedB.day, updatedB.unknownTime ? 12 : updatedB.hour, updatedB.unknownTime ? 0 : updatedB.minute, loc.longitude);

    const sajuA = calculateSaju(personA.year, personA.month, personA.day, personA.unknownTime ? null : corrA.correctedHour, corrA.correctedMinute, personA.unknownTime);
    const sajuB = calculateSaju(updatedB.year, updatedB.month, updatedB.day, updatedB.unknownTime ? null : corrB.correctedHour, corrB.correctedMinute, updatedB.unknownTime);

    setPersonA(p => ({ ...p, saju: sajuA, correction: corrA }));
    setPersonB(p => ({ ...p, location: loc, saju: sajuB, correction: corrB }));
    setGoonghapScores(calculateGoonghapScores(sajuA, sajuB));
    go('loading');
  };

  const handleLoadingComplete = () => go('result');
  const handleRestart = () => { setStep('intro'); setPersonA({ ...DEFAULT_PERSON }); setPersonB({ ...DEFAULT_PERSON, year: 1995, month: 7, day: 22 }); setGoonghapScores(null); };

  const isA = ['a_date','a_time','a_location'].includes(step);
  const isB = ['b_date','b_time','b_location'].includes(step);
  const showBanner = isA || isB;

  return (
    <div className={showBanner ? 'pt-12' : ''}>
      {showBanner && <PersonBanner label={isA ? 'A' : 'B'} name={isA ? nameA : nameB} />}

      {step === 'intro' && <GoonghapIntro onStart={handleIntroStart} />}

      {step === 'a_date' && (
        <BirthDateInput
          onNext={handleADate}
          defaultYear={personA.year} defaultMonth={personA.month} defaultDay={personA.day}
          defaultCalendarType={personA.calendarType}
        />
      )}
      {step === 'a_time' && (
        <BirthTimeInput
          onNext={handleATime} onBack={() => go('a_date')}
          defaultHour={personA.hour} defaultMinute={personA.minute}
        />
      )}
      {step === 'a_location' && (
        <BirthLocationInput onNext={handleALocation} onBack={() => go('a_time')} />
      )}

      {step === 'b_date' && (
        <BirthDateInput
          onNext={handleBDate}
          defaultYear={personB.year} defaultMonth={personB.month} defaultDay={personB.day}
          defaultCalendarType={personB.calendarType}
        />
      )}
      {step === 'b_time' && (
        <BirthTimeInput
          onNext={handleBTime} onBack={() => go('b_date')}
          defaultHour={personB.hour} defaultMinute={personB.minute}
        />
      )}
      {step === 'b_location' && (
        <BirthLocationInput onNext={handleBLocation} onBack={() => go('b_time')} />
      )}

      {step === 'loading' && (
        <LoadingAnimation
          year={personA.year}
          locationName={`${nameA}·${nameB}님의 사주`}
          onComplete={handleLoadingComplete}
        />
      )}

      {step === 'result' && goonghapScores && personA.saju && personB.saju && (
        <GoonghapResult
          nameA={nameA} nameB={nameB}
          scores={goonghapScores}
          sajuA={personA.saju}
          sajuB={personB.saju}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
